import {
  ModalBackGround
} from "./MJ5ABWDL.jsx";
import {
  checkAndInsert
} from "./TOTPBKQ7.jsx";
import {
  magicTag
} from "./ARBBQ34R.jsx";
import {
  useLockBodyScroll
} from "./2GLSLYXR.jsx";
import {
  cn,
  twc
} from "./O62HZC57.jsx";
import {
  useLocalization
} from "./E42DI7CC.jsx";
import {
  IconLoader
} from "./WHJJ2L4B.jsx";

// src/Dialog/Dialogs.tsx
import { For, Show, createSignal as createSignal2 } from "solid-js";
import { Dynamic } from "solid-js/web";

// src/utils/resource.ts
import { createEffect, createResource, createSignal, on, untrack } from "solid-js";
var createMutation = (fn) => {
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal(null);
  const dispatch = (...args) => {
    setError(null);
    setPending(true);
    return fn(...args).catch((err) => {
      setError(err);
      throw err;
    }).finally(() => setPending(false));
  };
  return Object.defineProperties(dispatch, {
    pending: { get: pending },
    loading: { get: pending },
    error: { get: error }
  });
};
function createLazyResource(pSource, pFetcher, pOptions) {
  const [trigger, setTrigger] = createSignal();
  let source;
  let fetcher;
  let options;
  if (arguments.length === 2 && typeof pFetcher === "object" || arguments.length === 1) {
    source = trigger;
    fetcher = pSource;
    options = pFetcher || {};
  } else {
    source = pSource;
    fetcher = pFetcher;
    options = pOptions || {};
  }
  const catchFetcher = async (...args) => {
    try {
      return await fetcher(...args);
    } catch (error) {
      return void 0;
    }
  };
  const [resource, { refetch, mutate }] = createResource(source, catchFetcher, options);
  const fetch = () => trigger() || setTrigger(true);
  const resourceAutoFetchWhenUse = Object.defineProperties(() => {
    untrack(() => fetch());
    return resource();
  }, Object.getOwnPropertyDescriptors(resource));
  return [resourceAutoFetchWhenUse, { fetch, refetch, mutate }];
}
var createTriggerResource = (pFetcher) => {
  const fetcher = async (...params) => {
    try {
      return await pFetcher(...params);
    } catch (error) {
      return void 0;
    }
  };
  const [trigger, setTrigger] = createSignal();
  const [resource, actions] = createResource(trigger, fetcher);
  const get = (trigger2) => setTrigger(() => trigger2);
  const forceGet = (pTrigger) => {
    const triggerValue = untrack(trigger);
    if (pTrigger === triggerValue) {
      actions.refetch();
      return;
    }
    get(pTrigger);
  };
  return [resource, get, Object.assign(actions, { forceGet, trigger })];
};
function createPoll(fn, timeout = 5e3, maxWait = 120 * 1e3) {
  return (...args) => {
    const { resolve, promise, reject } = Promise.withResolvers();
    let startTime = void 0;
    (async function _fn() {
      const timeNow = Date.now();
      if (!startTime) {
        startTime = timeNow;
      }
      if (timeNow - startTime > maxWait) {
        reject(new Error("Poll timeout"));
        return;
      }
      const result = await fn(...args).catch(() => false);
      if (result !== false) {
        resolve(result);
      } else {
        setTimeout(() => _fn(), timeout);
      }
    })();
    return promise;
  };
}
function createClientResource(pSource, pFetcher) {
  const [data, setData] = createSignal();
  const [error, setError] = createSignal();
  const [loading, setLoading] = createSignal(false);
  let fetcher;
  let source;
  if (arguments.length === 1) {
    if (typeof pSource !== "function") throw new Error("Invalid arguments: when only one argument, the argument must be a function");
    fetcher = pSource;
  } else {
    source = pSource;
    fetcher = pFetcher;
  }
  const getSourceValue = () => {
    return typeof source === "function" ? source() : source;
  };
  const fetch = async (sourceValue) => {
    setLoading(true);
    try {
      const result = await fetcher(sourceValue);
      setData(() => result);
    } catch (error2) {
      setError(error2);
    } finally {
      setLoading(false);
    }
  };
  createEffect(
    on(getSourceValue, () => {
      fetch(getSourceValue());
    })
  );
  const getData = () => {
    untrack(() => {
      if (!data() && !loading) {
        fetch(getSourceValue());
      }
    });
    return data();
  };
  const actions = {
    fetch: () => fetch(getSourceValue())
  };
  return [
    Object.assign(getData, {
      loading,
      error
    }),
    actions
  ];
}

// src/Dialog/store.ts
import { createUniqueId } from "solid-js";
import { createStore } from "solid-js/store";
var [dialogs, setDialogs] = createStore([]);
var remove = (id) => setDialogs((prev) => prev.filter((i) => i.id !== id));
var isDialogOptions = (x) => x != null && typeof x === "object" && "content" in x;
var create = (type, options) => new Promise((resolve) => {
  const opts = isDialogOptions(options) ? options : { content: options };
  const { singleDialogMode } = opts;
  if (singleDialogMode && dialogs.length > 0) {
    resolve(void 0);
    return;
  }
  const item = {
    ...opts,
    type: type ?? "alert",
    id: createUniqueId(),
    resolve: async (value) => {
      await item.onResolve?.(value);
      remove(item.id);
      resolve(value);
    }
  };
  checkAndInsert(magicTag("dialog"), Dialogs);
  setDialogs((prev) => [...prev, item]);
});
var alert = (options) => create("alert", options);
var confirm = (options) => create("confirm", options);
var prompt = (options) => create("prompt", options);

// src/Dialog/Dialogs.tsx
var Button = twc.button`inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none disabled:opacity-50 disabled:pointer-events-none`;
var Dialog = (props) => {
  const { t } = useLocalization();
  const [input, setInput] = createSignal2("");
  const handleCancel = createMutation(() => props.resolve(props.type === "prompt" ? void 0 : false));
  const handleDone = createMutation(() => props.resolve(props.type === "prompt" ? input() : true));
  return <ModalBackGround
    class={cn(
      "text-title-gray min-w-80 max-w-2xl space-y-4 rounded-lg bg-white p-6 px-5 py-4 text-sm shadow-lg sm:min-w-96",
      props.class
    )}
  >
      <div class="flex gap-2">
        <Show when={props.icon}>
          <div class="w-5">
            <Dynamic component={props.icon} class="text-text-level03 my-1 size-5" />
          </div>
        </Show>
        <div class="flex grow flex-col gap-2">
          <Show when={props.title}>
            <h2 class={cn("text-lg font-semibold capitalize", props.titleClass)}>{props.title}</h2>
          </Show>
          {typeof props.content === "function" ? props.content({ onDone: handleDone, onCancel: handleCancel }) : props.content}
          <Show when={props.type === "prompt"}>
            <input
    type="text"
    class="focus:border-primary w-full rounded-md border bg-transparent px-3 py-2 text-sm transition-colors focus:outline-none"
    value={input()}
    onInput={(e) => setInput(e.currentTarget.value)}
  />
          </Show>
        </div>
      </div>
      <div class={cn("flex justify-end gap-2", props.footerClass)}>
        <Show when={props.type !== "alert"}>
          <Button
    type="button"
    class="border-primary text-primary active:bg-primary/20 hover-allowed:hover:bg-primary/10 border capitalize"
    onClick={handleCancel}
    disabled={handleCancel.pending}
  >
            <Show when={handleCancel.pending} fallback={props.cancelText ?? t("Cancel")}>
              <IconLoader class="size-5 animate-spin" />
            </Show>
          </Button>
        </Show>
        <Button
    type="button"
    class="bg-primary active:bg-primary/80 hover-allowed:hover:bg-primary/90 min-w-24 text-white"
    onClick={handleDone}
    disabled={handleDone.pending || props?.confirmDisabled?.()}
  >
          <Show when={handleDone.pending} fallback={props.doneText ?? t(props.type === "alert" ? t("OK") : t("Yes"))}>
            <IconLoader class="size-5 animate-spin" />
          </Show>
        </Button>
      </div>
    </ModalBackGround>;
};
var Dialogs = () => {
  useLockBodyScroll(() => dialogs.length > 0);
  return <div id={magicTag("dialog")}>
      <Show when={dialogs.length}>
        <For each={dialogs}>{Dialog}</For>
      </Show>
    </div>;
};

export {
  createMutation,
  createLazyResource,
  createTriggerResource,
  createPoll,
  createClientResource,
  alert,
  confirm,
  prompt,
  Dialogs
};
